import { TimerTextStack } from '../../components/display/TimerTextStack';
import { unifyTimerLabels } from '../../lib/timerLabelSettings';
import { timerAudioFromOptions } from '../../lib/timerAudioConfig';
import { brand } from '../../lib/brand';
import { currentBlinds, formatForcedBets } from '../../lib/forcedBets';
import { nextPlayableLevel } from '../../lib/nextPlayableLevel';
import { tournamentDuration } from '../../lib/tournamentDuration';
import { TimerCountdown } from '../../components/display/TimerCountdown';
import { nextBreakSeconds } from '../../lib/nextBreakCountdown';
import { TimerMarquee } from '../../components/display/TimerMarquee';
import { TimerHeader, FittedText } from '../../components/display/TimerHeader';
import { TimerStatCard } from '../../components/display/TimerStatCard';
import { FittedLines } from '../../components/display/FittedLines';
import { formatTimerClock } from '../../lib/timerClockFormat';
import { adaptTimerHeaderLayout, DEFAULT_TIMER_LAYOUT } from '../../lib/timerHeaderLayout';
import { GridLines } from '../../components/display/GridLines';
import { gridRect } from '../../lib/gridGeometry';
import { tvMetrics } from '../../lib/tvMetrics';
import { observeResize } from '../../lib/observeResize';
import { TvSafeArea } from '../../components/display/TvSafeArea';
import { isTvAcceptanceEnabled, saveDisplaySample } from '../../lib/tvAcceptance';
/**
 * @page TVScreen
 * @description 電視全螢幕顯示頁面（Token URL 公開存取）
 * Route: /display/screen/:token
 * 依 DisplayScreen.layout 切換顯示模式
 * 三種計時器模式：Active Level / Break / Registration
 * 參照 Smart Timer 設計稿：三欄式佈局
 */

import { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
// i18n removed — all display text is English
import { useDisplaySocket } from '../../hooks/useDisplaySocket';
import type { DisplayTimerPayload, LeaderboardEntry } from '../../hooks/useDisplaySocket';
import { formatTime } from '../../lib/format';
import { THEMES, isValidTheme, type TimerTheme, type ThemeTokens } from '../../lib/timerThemes';
import { useFullscreen } from '../../hooks/useFullscreen';
import { useTvScale } from '../../hooks/useTvScale';
import { useSiteName } from '../../hooks/useSiteName';
import { getDisplayLevel, type TournamentType } from '@pokerroom/types';
import { timeoutSignal } from '../../lib/timeoutSignal';
import { getDisplayPrizePool } from '../../lib/displayPrizePool';
import { calculateDeadlineCountdown } from '../../lib/lateRegistration';
import {
  clearDisplaySessionCredential,
  readDisplaySessionCredential,
} from '../../lib/displaySession';

function useVenueLogo(venue?: string): string | null {
  const [logo, setLogo] = useState<string | null>(brand.logoDark);
  useEffect(() => {
    setLogo(brand.logoDark);
    if (!venue) return;
    let current = true;
    const apiBase =
      (import.meta as unknown as { env: { VITE_API_URL?: string } }).env.VITE_API_URL ?? '';
    const query = `venue=${encodeURIComponent(venue)}`;
    fetch(`${apiBase}/api/branding/tv_logo?${query}`, { signal: timeoutSignal(10000) })
      .then((r) => (r.ok ? r.json() : null))
      .then((json) => {
        const data = json?.data ?? json;
        if (current && data?.filePath)
          setLogo(
            `${apiBase}/api/branding/tv_logo/file?${query}&v=${encodeURIComponent(data.updatedAt ?? '')}`,
          );
      })
      .catch((err) => console.warn('[TVScreen] Failed to fetch logo:', err));
    return () => {
      current = false;
    };
  }, [venue]);
  return logo;
}

function useElapsedTime(payload: DisplayTimerPayload | null, terminal: boolean): number | null {
  const [elapsed, setElapsed] = useState<number | null>(() =>
    payload?.elapsedMs === undefined ? null : Math.floor(payload.elapsedMs / 1000),
  );
  useEffect(() => {
    if (payload?.elapsedMs === undefined) {
      setElapsed(null);
      return;
    }
    const base = payload.elapsedMs;
    const receivedAt = performance.now();
    const tick = () =>
      setElapsed(
        Math.floor(
          (base +
            (payload.isRunning && !terminal
              ? Math.min(
                  Math.max(0, performance.now() - receivedAt),
                  Math.max(0, payload.remainingSeconds * 1000),
                )
              : 0)) /
            1000,
        ),
      );
    tick();
    if (!payload.isRunning || terminal) return;
    const interval = setInterval(tick, 250);
    return () => clearInterval(interval);
  }, [
    payload?.elapsedMs,
    payload?.sampledAt,
    payload?.isRunning,
    payload?.remainingSeconds,
    terminal,
  ]);
  return elapsed;
}

interface BlindLevel {
  levelNo: number;
  isBreak: boolean;
  durationMinutes?: number;
  durationSecs?: number | null;
  smallBlind?: number;
  bigBlind?: number;
  ante?: number;
  bba?: number;
  note?: string;
}

interface TournamentInfo {
  type?: TournamentType;
  completedAt?: string | null;
  id: string;
  name: string;
  status: string;
  prizePool: number;
  displayPrizePool?: number;
  activePlayers: number;
  totalEntries: number;
  lateRegLevels?: number;
  rebuyAllowed?: boolean;
  rebuyDeadline?: number;
  addonAllowed?: boolean;
  addonDeadline?: number;
  startingChips?: number;
  rebuyChips?: number;
  addonChips?: number;
  startedAt?: string | null;
  displaySideInfo?: string[] | null;
  displayMarquee?: string | null;
  blindStructure?: {
    levelSecs: number;
    levels: BlindLevel[];
  };
  payoutSlots?: Array<{
    rank: number;
    amount: number;
    percentage: number;
  }>;
}

interface DisplayScreen {
  venue?: { code: string; name: string };
  id: string;
  name: string;
  tournamentId: string | null;
  layout: string;
  isActive: boolean;
  insetPercent?: number;
}

// ─── Shared Helpers ─────────────────────────────────────────────────────────

function useCountdown(remainingSeconds: number, isRunning: boolean) {
  const [display, setDisplay] = useState(remainingSeconds);

  useEffect(() => {
    setDisplay(remainingSeconds);
  }, [remainingSeconds]);

  useEffect(() => {
    if (!isRunning) return;
    const interval = setInterval(() => {
      setDisplay((prev) => Math.max(0, prev - 1));
    }, 1000);
    return () => clearInterval(interval);
  }, [isRunning, remainingSeconds]);

  return display;
}

function getDeadlineDisplay(
  allowed: boolean | undefined,
  deadline: number | undefined,
  currentLevel: number,
): string | null {
  if (!allowed) return null;
  if (deadline != null && currentLevel <= deadline) return `Lv ${deadline}`;
  return 'CLOSED';
}

function useCurrentClock() {
  const fmt = () => {
    const d = new Date();
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}:${String(d.getSeconds()).padStart(2, '0')}`;
  };
  const [clock, setClock] = useState(fmt);
  useEffect(() => {
    const interval = setInterval(() => setClock(fmt()), 1000);
    return () => clearInterval(interval);
  }, []);
  return clock;
}

// ─── Registration Mode ──────────────────────────────────────────────────────

/**
 * 場上平均籌碼 = 場上總籌碼 ÷ 剩餘人數。
 *
 * rebuy 與 addon 各有自己的籌碼數，不能一律套用 startingChips；
 * 當賽事資料尚未載入時回退為 startingChips，維持既有行為。
 */
function computeAvgStack(args: {
  activePlayers: number;
  totalEntries: number;
  rebuyCount: number;
  addonCount: number;
  startingChips: number;
  rebuyChips?: number;
  addonChips?: number;
}): number {
  const { activePlayers, totalEntries, rebuyCount, addonCount, startingChips } = args;
  if (activePlayers <= 0 || startingChips <= 0) return 0;
  const rebuyChips = args.rebuyChips ?? startingChips;
  const addonChips = args.addonChips ?? startingChips;
  const totalChips =
    totalEntries * startingChips + rebuyCount * rebuyChips + addonCount * addonChips;
  return Math.round(totalChips / activePlayers);
}

// ─── Active Timer Display (1080p Optimized) ────────────────────────────────

const MONO_STYLE = {
  fontFamily: 'var(--font-mono)',
  fontVariantNumeric: 'tabular-nums',
} as const;

// ─── Layout Config (shared with TimerPreviewPage editor) ─────────────────

type LayoutItem = { i: string; x: number; y: number; w: number; h: number };

interface LayoutSlot {
  layout: LayoutItem[];
  sideInfoLines?: string[];
  marqueeText?: string;
  fontSizes?: Record<string, number>;
}

interface TimerLayoutConfig {
  theme?: TimerTheme;
  layouts?: Record<TimerTheme, LayoutSlot>;
  options?: Record<string, unknown>;
  audioReady?: boolean;
}

const DEFAULT_TV_FONT_SIZES: Record<string, number> = {
  logo: 48,
  logoLabel: 20,
  logoName: 20,
  clock: 30,
  lateReg: 20,
  level: 30,
  timer: 200,
  blinds: 60,
  blindsLabel: 20,
  ante: 40,
  nextInfo: 20,
  statLabel: 20,
  entries: 40,
  avgStack: 40,
  prizePool: 40,
  nextLevel: 40,
  rebuyAddon: 40,
  elapsed: 40,
  sideInfo: 26,
  marquee: 26,
};

const DEFAULT_TV_LAYOUT: LayoutItem[] = DEFAULT_TIMER_LAYOUT;

function makeDefaultTVSlot(themeName: TimerTheme): LayoutSlot {
  return {
    layout: DEFAULT_TV_LAYOUT,
    sideInfoLines: [],
    marqueeText: '',
    fontSizes: unifyTimerLabels(
      { ...DEFAULT_TV_FONT_SIZES, ...(THEMES[themeName].defaultFontSizes ?? {}) },
      themeName,
    ),
  };
}

const DEFAULT_TV_CONFIG: TimerLayoutConfig = {
  theme: 'custom',
  layouts: { custom: makeDefaultTVSlot('custom'), pokerfans: makeDefaultTVSlot('pokerfans') },
};

function isValidLayoutArr(arr: unknown[]): boolean {
  return arr.length > 0 && typeof arr[0] === 'object' && arr[0] !== null && 'i' in arr[0];
}

export function migrateTVConfig(raw: unknown): TimerLayoutConfig | null {
  if (!raw) return null;
  // Very old format: flat array
  if (Array.isArray(raw)) {
    if (isValidLayoutArr(raw)) {
      const slot: LayoutSlot = {
        layout: raw as LayoutItem[],
        sideInfoLines: [],
        marqueeText: '',
        fontSizes: {},
      };
      return {
        theme: 'custom',
        layouts: { custom: slot, pokerfans: makeDefaultTVSlot('pokerfans') },
      };
    }
    return null;
  }
  const r = raw as {
    theme?: unknown;
    layouts?: unknown;
    layout?: unknown;
    sideInfoLines?: unknown;
    marqueeText?: unknown;
    fontSizes?: unknown;
    options?: unknown;
  };
  const theme: TimerTheme = isValidTheme(r.theme) ? r.theme : 'custom';
  const options =
    r.options && typeof r.options === 'object' && !Array.isArray(r.options)
      ? (r.options as Record<string, unknown>)
      : {};
  // New shape
  if (r.layouts && typeof r.layouts === 'object') {
    const ls = r.layouts as Record<string, unknown>;
    const sanitize = (s: unknown, slotTheme: TimerTheme): LayoutSlot => {
      const o = (s ?? {}) as Partial<LayoutSlot>;
      const layout =
        Array.isArray(o.layout) && isValidLayoutArr(o.layout) ? o.layout : DEFAULT_TV_LAYOUT;
      return {
        layout,
        sideInfoLines: o.sideInfoLines ?? [],
        marqueeText: o.marqueeText ?? '',
        fontSizes: {
          ...makeDefaultTVSlot(slotTheme).fontSizes,
          ...unifyTimerLabels(o.fontSizes ?? {}, slotTheme),
        },
      };
    };
    return {
      theme,
      options,
      layouts: {
        custom: sanitize(ls.custom, 'custom'),
        pokerfans: sanitize(ls.pokerfans, 'pokerfans'),
      },
    };
  }
  // Legacy flat shape
  if (Array.isArray(r.layout) && isValidLayoutArr(r.layout)) {
    const slot: LayoutSlot = {
      layout: r.layout as LayoutItem[],
      sideInfoLines: Array.isArray(r.sideInfoLines) ? (r.sideInfoLines as string[]) : [],
      marqueeText: typeof r.marqueeText === 'string' ? r.marqueeText : '',
      fontSizes:
        r.fontSizes && typeof r.fontSizes === 'object'
          ? (r.fontSizes as Record<string, number>)
          : {},
    };
    const other: TimerTheme = theme === 'custom' ? 'pokerfans' : 'custom';
    return {
      theme,
      options,
      layouts: { [theme]: slot, [other]: makeDefaultTVSlot(other) } as Record<
        TimerTheme,
        LayoutSlot
      >,
    };
  }
  return { ...DEFAULT_TV_CONFIG, theme, options };
}

function useTimerLayoutConfig(): TimerLayoutConfig {
  const [config, setConfig] = useState<TimerLayoutConfig>(DEFAULT_TV_CONFIG);
  const [audioReady, setAudioReady] = useState(false);
  useEffect(() => {
    const apiBase =
      (import.meta as unknown as { env: { VITE_API_URL?: string } }).env.VITE_API_URL ?? '';
    fetch(`${apiBase}/api/display/timer-layout`, { signal: timeoutSignal(10000) })
      .then((r) => (r.ok ? (r.json() as Promise<{ data?: { config?: unknown } }>) : null))
      .then((d) => {
        if (!d) return;
        const migrated = migrateTVConfig(d.data?.config);
        if (migrated) setConfig(migrated);
      })
      .catch((err) => console.warn('[TVScreen] Failed to fetch display config:', err))
      .finally(() => setAudioReady(true));
  }, []);
  return { ...config, audioReady };
}

// ─── Grid-based TimerDisplay (uses saved layout from editor) ─────────────

export function GridTimerDisplay({
  timerPayload,
  tournament,
  logo,
  config,
}: {
  timerPayload: DisplayTimerPayload | null;
  tournament: TournamentInfo | null;
  logo: string | null;
  config: TimerLayoutConfig;
}) {
  const siteName = useSiteName();
  const remainingSeconds = timerPayload?.remainingSeconds ?? 0;
  const isRunning = timerPayload?.isRunning ?? false;
  const countdown = useCountdown(remainingSeconds, isRunning);
  const isNotStarted =
    (tournament?.status === 'DRAFT' || tournament?.status === 'REGISTERED') &&
    !isRunning &&
    !(timerPayload?.startedAt ?? tournament?.startedAt);

  const level = timerPayload?.level ?? 1;
  const levels = tournament?.blindStructure?.levels ?? [];
  const displayLevel = getDisplayLevel(levels, level);
  const sb = timerPayload?.smallBlind ?? 0;
  const bb = timerPayload?.bigBlind ?? 0;
  const ante = timerPayload?.ante ?? 0;
  const bba = levels.find((entry) => entry.levelNo === level)?.bba ?? 0;
  const blinds = currentBlinds(sb, bb, ante, bba, timerPayload?.isBreak);
  const nextSb = timerPayload?.nextSmallBlind;
  const nextBb = timerPayload?.nextBigBlind;
  const nextAnte = timerPayload?.nextAnte;
  const nextLevelNo = timerPayload?.nextLevel;
  const nextBba = levels.find((entry) => entry.levelNo === nextLevelNo)?.bba ?? 0;
  const nextDisplayLevel = nextLevelNo == null ? null : getDisplayLevel(levels, nextLevelNo);
  const nextIsBreak = timerPayload?.nextIsBreak ?? false;
  const postBreak =
    nextIsBreak && nextLevelNo != null ? nextPlayableLevel(levels, nextLevelNo) : null;
  const activePlayers = timerPayload?.activePlayers ?? tournament?.activePlayers ?? 0;
  const totalEntries = timerPayload?.totalEntries ?? tournament?.totalEntries ?? 0;
  const name = timerPayload?.tournamentName ?? tournament?.name ?? '';
  const rebuyCount = timerPayload?.rebuyCount ?? 0;
  const addonCount = timerPayload?.addonCount ?? 0;
  const startingChips = timerPayload?.startingChips ?? 0;
  const terminal = tournament?.status === 'COMPLETED' || tournament?.status === 'CANCELLED';
  const elapsed = useElapsedTime(timerPayload, terminal);
  const duration = tournamentDuration(
    tournament?.type,
    levels,
    tournament?.blindStructure?.levelSecs ?? 0,
    elapsed ?? 0,
    timerPayload ? { level, remaining: countdown } : undefined,
  );
  const lateRegLevels = timerPayload?.lateRegLevels ?? tournament?.lateRegLevels;
  const isWarning = countdown <= 60 && isRunning;
  const lateRegCountdown = calculateDeadlineCountdown({
    levels,
    levelSecs: tournament?.blindStructure?.levelSecs ?? 0,
    currentLevelNo: level,
    currentRemainingSeconds: countdown,
    deadlineDisplayLevel: lateRegLevels,
  });
  const nextBreakSecs = nextBreakSeconds(
    levels,
    level,
    countdown,
    tournament?.blindStructure?.levelSecs ?? 0,
    !terminal && !isNotStarted && Boolean(timerPayload) && !timerPayload?.isBreak,
  );

  const rebuyChips = tournament?.rebuyChips;
  const addonChips = tournament?.addonChips;
  const avgStack = useMemo(
    () =>
      computeAvgStack({
        activePlayers,
        totalEntries,
        rebuyCount,
        addonCount,
        startingChips,
        rebuyChips,
        addonChips,
      }),
    [activePlayers, totalEntries, rebuyCount, addonCount, startingChips, rebuyChips, addonChips],
  );

  const activeTheme: TimerTheme = config.theme ?? 'custom';
  const slot: LayoutSlot = config.layouts?.[activeTheme] ?? makeDefaultTVSlot(activeTheme);
  const fs = unifyTimerLabels(slot.fontSizes ?? {}, activeTheme);
  // Side info + marquee: prefer tournament data, fallback to layout config
  const sideLines: string[] =
    (tournament?.displaySideInfo as string[] | null) ?? slot.sideInfoLines ?? [];
  const marqueeText: string = tournament?.displayMarquee ?? slot.marqueeText ?? '';
  const layout = adaptTimerHeaderLayout(slot.layout);
  const theme: ThemeTokens = THEMES[activeTheme];
  const monoStyle = {
    fontFamily: theme.fontFamily,
    fontVariantNumeric: 'tabular-nums',
    letterSpacing: theme.letterSpacing,
  } as const;

  // Grid: 12 cols, rowHeight scales with viewport (ref: 1080p ≈ 13 rows)
  const gridRef = useRef<HTMLDivElement>(null);
  const [gridSize, setGridSize] = useState({ width: 0, height: 0 });
  useEffect(() => {
    const element = gridRef.current;
    if (!element) return;
    const update = () => setGridSize({ width: element.clientWidth, height: element.clientHeight });
    return observeResize(element, update);
  }, []);
  const { rowHeight: ROW_HEIGHT, scale: tvScale } = tvMetrics(gridSize.height, 0, gridSize.width);
  const sf = (px: number) => Math.round(px * tvScale);

  const widgets: Record<string, React.ReactNode> = {
    logo: (
      <TimerHeader
        separateTitle={layout.some((item) => item.i === 'tournamentName')}
        logoUrl={logo}
        siteName={siteName}
        name={name}
        theme={theme}
        fontSizes={fs}
        scale={tvScale}
      />
    ),
    tournamentName: (
      <TimerHeader
        titleOnly
        separateTitle
        logoUrl={logo}
        siteName={siteName}
        name={name}
        theme={theme}
        fontSizes={fs}
        scale={tvScale}
      />
    ),
    lateReg: (() => {
      return (
        <TimerStatCard
          label="Late Reg"
          value={
            lateRegCountdown.isOpen ? formatTimerClock(lateRegCountdown.remainingSeconds) : 'CLOSED'
          }
          fontSize={sf(fs.clock ?? 18)}
          labelSize={sf(fs.lateReg ?? 12)}
          theme={theme}
          labelColor={theme.primaryColor}
          valueColor={lateRegCountdown.isOpen ? theme.statValueColor : theme.timerWarningColor}
        />
      );
    })(),
    level: (
      <div
        className="flex items-center justify-center h-full min-w-0 min-h-0 p-1"
        style={{ backgroundColor: theme.widgetBg }}
      >
        <div
          className="rounded-xl w-full h-full min-w-0 min-h-0 px-2 py-1"
          style={{ backgroundColor: theme.accentBg }}
        >
          <FittedText
            text={`LEVEL ${displayLevel}`}
            size={sf(fs.level ?? 28)}
            color={theme.statValueColor}
            className="tracking-wider"
          />
        </div>
      </div>
    ),
    timer: (
      <TimerCountdown
        seconds={countdown}
        isBreak={timerPayload?.isBreak ?? false}
        isWarning={isWarning}
        statusLabel={isNotStarted ? '尚未開始' : undefined}
        theme={theme}
        fontSize={sf(fs.timer ?? 200)}
      />
    ),
    blinds: (
      <div className="h-full w-full min-h-0 min-w-0 px-1" style={{ background: theme.blindsBg }}>
        <TimerTextStack
          rows={[
            {
              text: blinds.label,
              size: sf(fs.blindsLabel ?? 12),
              color: theme.blindsLabelColor,
              className: 'uppercase tracking-[0.3em]',
            },
            {
              text: blinds.value,
              size: sf(fs.blinds ?? 48),
              color: theme.blindsValueColor,
              className: 'font-black tracking-tight',
              style: monoStyle,
            },
            ...(postBreak || nextBb
              ? [
                  {
                    text: postBreak
                      ? `After break: Lv ${postBreak.displayLevel} — ${postBreak.smallBlind?.toLocaleString()} / ${postBreak.bigBlind?.toLocaleString()}${formatForcedBets(postBreak.ante, postBreak.bba)}`
                      : `Next: ${nextIsBreak ? 'BREAK' : `Lv ${nextDisplayLevel} — ${nextSb?.toLocaleString()} / ${nextBb?.toLocaleString()}`}${!nextIsBreak ? formatForcedBets(nextAnte, nextBba) : ''}`,
                    size: sf(fs.nextInfo ?? 16),
                    color: theme.nextInfoColor,
                  },
                ]
              : []),
          ]}
        />
      </div>
    ),
    entries: (
      <TimerStatCard
        label="Players"
        value={`${activePlayers} / ${totalEntries}`}
        fontSize={sf(fs.entries ?? 28)}
        labelSize={sf(fs.statLabel ?? 11)}
        theme={theme}
      />
    ),
    avgStack: (
      <TimerStatCard
        label="Avg Stack"
        value={avgStack > 0 ? avgStack.toLocaleString() : '—'}
        fontSize={sf(fs.avgStack ?? 28)}
        labelSize={sf(fs.statLabel ?? 11)}
        theme={theme}
      />
    ),
    prizePool: (
      <TimerStatCard
        label="Prize Pool"
        value={`NT$ ${getDisplayPrizePool(timerPayload, tournament).toLocaleString()}`}
        fontSize={sf(fs.prizePool ?? 28)}
        labelSize={sf(fs.statLabel ?? 11)}
        theme={theme}
        valueColor={theme.prizePoolValueColor}
      />
    ),
    nextLevel: (
      <TimerStatCard
        label={nextIsBreak ? 'Next' : 'Next Level'}
        value={nextDisplayLevel == null ? '—' : nextIsBreak ? 'Break' : `Lv ${nextDisplayLevel}`}
        fontSize={sf(fs.nextLevel ?? 28)}
        labelSize={sf(fs.statLabel ?? 11)}
        theme={theme}
      />
    ),
    rebuyAddon: (
      <TimerStatCard
        label="Rebuy / Addon"
        value={`${rebuyCount} / ${addonCount}`}
        fontSize={sf(fs.rebuyAddon ?? 28)}
        labelSize={sf(fs.statLabel ?? 11)}
        theme={theme}
      />
    ),
    elapsed: (
      <TimerStatCard
        label={duration.label}
        value={duration.label === 'Elapsed' && elapsed === null ? '—' : duration.value}
        fontSize={sf(fs.elapsed ?? 28)}
        labelSize={sf(fs.statLabel ?? 11)}
        theme={theme}
        secondary={
          nextBreakSecs !== null ? `Break in ${formatTimerClock(nextBreakSecs)}` : undefined
        }
      />
    ),
    sideInfo: (
      <div className="h-full min-h-0 min-w-0 px-4 py-3" style={{ backgroundColor: theme.widgetBg }}>
        <FittedLines
          lines={sideLines.length ? sideLines : ['No info configured']}
          size={sf(fs.sideInfo ?? 16)}
          color={sideLines.length ? theme.sideInfoColor : `${theme.sideInfoColor}66`}
        />
      </div>
    ),
  };

  const containerStyle: React.CSSProperties = {
    backgroundColor: theme.bgColor,
    backgroundImage: theme.bgPattern
      ? theme.bgImage
        ? `${theme.bgPattern}, ${theme.bgImage}`
        : theme.bgPattern
      : theme.bgImage,
    backgroundSize: theme.bgSize,
  };

  return (
    <div
      ref={gridRef}
      className="text-white h-screen w-screen overflow-hidden relative"
      style={containerStyle}
    >
      {layout.map((item) => {
        if (item.i === 'marquee') return null; // rendered separately below
        const widget = widgets[item.i];
        if (!widget) return null;
        const rect = gridRect(item, gridSize.width, ROW_HEIGHT);
        return (
          <div
            key={item.i}
            data-widget={item.i}
            className="absolute overflow-hidden"
            style={{
              left: rect.x,
              width: rect.width,
              top: rect.y,
              height: rect.height,
              padding: `${theme.gridGapPx ?? 1}px`,
            }}
          >
            <div className="h-full w-full">{widget}</div>
          </div>
        );
      })}
      {/* Marquee: full-width at layout-configured vertical position. Height from layout config. */}
      {(() => {
        const marqueeItem = layout.find((item) => item.i === 'marquee');
        if (!marqueeItem || (!marqueeText && nextBreakSecs === null)) return null;
        const rect = gridRect(marqueeItem, gridSize.width, ROW_HEIGHT);
        return (
          <div
            className="absolute flex items-center overflow-hidden z-10"
            style={{
              left: rect.x,
              width: rect.width,
              top: rect.y,
              height: rect.height,
              backgroundColor: theme.marqueeBg,
            }}
          >
            <TimerMarquee
              text={marqueeText}
              breakSeconds={nextBreakSecs}
              size={sf(fs.marquee ?? 24)}
              color={theme.marqueeColor}
            />
          </div>
        );
      })()}
      <GridLines
        rects={layout
          .filter((item) =>
            item.i === 'marquee'
              ? Boolean(marqueeText || nextBreakSecs !== null)
              : Boolean(widgets[item.i]),
          )
          .map((item) => gridRect(item, gridSize.width, ROW_HEIGHT))}
        width={gridSize.width}
        height={gridSize.height}
        color={theme.widgetBorder}
        lineWidth={theme.borderWidthPx ?? 1}
      />
    </div>
  );
}

export function TimerDisplay({
  timerPayload,
  tournament,
  logo,
}: {
  timerPayload: DisplayTimerPayload | null;
  tournament: TournamentInfo | null;
  logo: string | null;
}) {
  const siteName = useSiteName();
  const remainingSeconds = timerPayload?.remainingSeconds ?? 0;
  const isRunning = timerPayload?.isRunning ?? false;
  const countdown = useCountdown(remainingSeconds, isRunning);
  const clock = useCurrentClock();

  const level = timerPayload?.level ?? 1;
  const levels = tournament?.blindStructure?.levels ?? [];
  const displayLevel = getDisplayLevel(levels, level);
  const sb = timerPayload?.smallBlind ?? 0;
  const bb = timerPayload?.bigBlind ?? 0;
  const ante = timerPayload?.ante ?? 0;
  const bba = levels.find((entry) => entry.levelNo === level)?.bba ?? 0;
  const blinds = currentBlinds(sb, bb, ante, bba, timerPayload?.isBreak);
  const nextSb = timerPayload?.nextSmallBlind;
  const nextBb = timerPayload?.nextBigBlind;
  const nextAnte = timerPayload?.nextAnte;
  const nextLevelNo = timerPayload?.nextLevel;
  const nextBba = levels.find((entry) => entry.levelNo === nextLevelNo)?.bba ?? 0;
  const nextDisplayLevel = nextLevelNo == null ? null : getDisplayLevel(levels, nextLevelNo);
  const nextIsBreak = timerPayload?.nextIsBreak ?? false;
  const postBreak =
    nextIsBreak && nextLevelNo != null ? nextPlayableLevel(levels, nextLevelNo) : null;
  const activePlayers = timerPayload?.activePlayers ?? tournament?.activePlayers ?? 0;
  const totalEntries = timerPayload?.totalEntries ?? tournament?.totalEntries ?? 0;
  const name = timerPayload?.tournamentName ?? tournament?.name ?? '';
  const rebuyCount = timerPayload?.rebuyCount ?? 0;
  const addonCount = timerPayload?.addonCount ?? 0;
  const startingChips = timerPayload?.startingChips ?? 0;
  const lateRegLevels = timerPayload?.lateRegLevels ?? tournament?.lateRegLevels;

  const rebuyChips = tournament?.rebuyChips;
  const addonChips = tournament?.addonChips;
  const avgStack = useMemo(
    () =>
      computeAvgStack({
        activePlayers,
        totalEntries,
        rebuyCount,
        addonCount,
        startingChips,
        rebuyChips,
        addonChips,
      }),
    [activePlayers, totalEntries, rebuyCount, addonCount, startingChips, rebuyChips, addonChips],
  );

  const isWarning = countdown <= 60 && isRunning;
  const lateRegCountdown = calculateDeadlineCountdown({
    levels,
    levelSecs: tournament?.blindStructure?.levelSecs ?? 0,
    currentLevelNo: level,
    currentRemainingSeconds: countdown,
    deadlineDisplayLevel: lateRegLevels,
  });

  const rebuyDeadlineText = getDeadlineDisplay(
    tournament?.rebuyAllowed,
    tournament?.rebuyDeadline,
    displayLevel,
  );
  const addonDeadlineText = getDeadlineDisplay(
    tournament?.addonAllowed,
    tournament?.addonDeadline,
    displayLevel,
  );
  const nextBreakSecs = nextBreakSeconds(
    levels,
    level,
    countdown,
    tournament?.blindStructure?.levelSecs ?? 0,
    Boolean(timerPayload) &&
      !timerPayload?.isBreak &&
      !['DRAFT', 'REGISTERED', 'COMPLETED', 'CANCELLED'].includes(tournament?.status ?? ''),
  );
  const { scale: tvScale } = useTvScale();
  const sf = (px: number) => Math.round(px * tvScale);

  return (
    <div className="bg-[var(--brand-night)] text-white h-screen w-screen flex flex-col overflow-hidden">
      {/* ── Header ── */}
      <header
        className="flex-none border-b border-[#1a3a2a] flex items-center px-6 justify-between bg-[#0d1f18]"
        style={{ height: sf(56) }}
      >
        <div className="flex items-center gap-3">
          {logo ? (
            <img src={logo} alt="Logo" className="object-contain" style={{ height: sf(36) }} />
          ) : (
            <span
              className="material-symbols-outlined text-[var(--white)]"
              style={{ fontSize: sf(24) }}
            >
              style
            </span>
          )}
          <div>
            <p
              className="font-bold text-[var(--white)] uppercase tracking-widest leading-none"
              style={{ fontSize: sf(10) }}
            >
              {siteName}
            </p>
            <p className="text-white font-bold leading-tight" style={{ fontSize: sf(20) }}>
              {name}
            </p>
          </div>
        </div>
        <div className="flex flex-col items-center justify-center gap-0.5">
          <span
            className="bg-[var(--white)]/15 border border-[var(--white)]/40 text-[var(--white)] font-bold px-3 py-0.5 rounded-full uppercase tracking-wider"
            style={{ fontSize: sf(12) }}
          >
            Late Reg
          </span>
          {lateRegCountdown.isOpen ? (
            <span className="text-white font-bold" style={{ ...MONO_STYLE, fontSize: sf(16) }}>
              {formatTime(lateRegCountdown.remainingSeconds)}
            </span>
          ) : (
            <span className="text-red-400 font-bold" style={{ fontSize: sf(16) }}>
              CLOSED
            </span>
          )}
        </div>
      </header>

      {/* ── Middle: Left + Center + Right ── */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Stats */}
        <aside
          className="flex-none bg-[#0d1f18] border-r border-[#1a3a2a] flex flex-col justify-center gap-5 px-4 py-4"
          style={{ width: sf(180) }}
        >
          <div className="rounded-xl border border-[#1a3a2a] bg-[var(--brand-night)] p-4 text-center">
            <p
              className="font-bold text-slate-500 uppercase tracking-widest mb-1"
              style={{ fontSize: sf(10) }}
            >
              Rebuy
            </p>
            <p
              className="font-black text-white leading-tight"
              style={{ ...MONO_STYLE, fontSize: sf(28) }}
            >
              {rebuyCount}
            </p>
          </div>
          <div className="rounded-xl border border-[#1a3a2a] bg-[var(--brand-night)] p-4 text-center">
            <p
              className="font-bold text-slate-500 uppercase tracking-widest mb-1"
              style={{ fontSize: sf(10) }}
            >
              Addon
            </p>
            <p
              className="font-black text-white leading-tight"
              style={{ ...MONO_STYLE, fontSize: sf(28) }}
            >
              {addonCount}
            </p>
          </div>
          <div className="rounded-xl border border-[#1a3a2a] bg-[var(--brand-night)] p-4 text-center">
            <p
              className="font-bold text-slate-500 uppercase tracking-widest mb-1"
              style={{ fontSize: sf(10) }}
            >
              Avg Stack
            </p>
            <p
              className="font-black text-white leading-tight"
              style={{ ...MONO_STYLE, fontSize: sf(28) }}
            >
              {avgStack > 0 ? avgStack.toLocaleString() : '—'}
            </p>
          </div>
        </aside>

        {/* Center Timer (flex-1) */}
        <main className="flex-1 flex flex-col items-center justify-center relative bg-[var(--brand-night)]">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(218,166,11,0.06)_0%,_transparent_70%)]" />
          <div className="relative z-10 flex flex-col items-center gap-4">
            {/* Level pill */}
            <div className="bg-[#15803d] rounded-xl px-8 py-2">
              <span className="text-white font-bold tracking-wider" style={{ fontSize: sf(24) }}>
                LEVEL {displayLevel}
              </span>
            </div>
            {/* Timer */}
            <div
              className="leading-none font-black tracking-tight"
              style={{
                ...MONO_STYLE,
                fontSize: `clamp(${sf(140)}px, 22vh, ${sf(240)}px)`,
                color: isWarning ? '#ef4444' : 'var(--white)',
                textShadow: `0 0 60px ${isWarning ? 'rgba(239,68,68,0.25)' : 'rgba(218,166,11,0.2)'}`,
              }}
            >
              {formatTime(countdown)}
            </div>
          </div>
        </main>

        {/* Right Stats */}
        <aside
          className="flex-none bg-[#0d1f18] border-l border-[#1a3a2a] flex flex-col justify-center gap-5 px-4 py-4"
          style={{ width: sf(180) }}
        >
          <div className="rounded-xl border border-[#1a3a2a] bg-[var(--brand-night)] p-4 text-center">
            <p
              className="font-bold text-slate-500 uppercase tracking-widest mb-1"
              style={{ fontSize: sf(10) }}
            >
              Rebuy Cutoff
            </p>
            <p
              className={`font-black leading-tight ${rebuyDeadlineText === 'CLOSED' ? 'text-red-500' : 'text-white'}`}
              style={{ ...MONO_STYLE, fontSize: sf(28) }}
            >
              {rebuyDeadlineText ?? '—'}
            </p>
          </div>
          <div className="rounded-xl border border-[#1a3a2a] bg-[var(--brand-night)] p-4 text-center">
            <p
              className="font-bold text-slate-500 uppercase tracking-widest mb-1"
              style={{ fontSize: sf(10) }}
            >
              Addon Cutoff
            </p>
            <p
              className={`font-black leading-tight ${addonDeadlineText === 'CLOSED' ? 'text-red-500' : 'text-white'}`}
              style={{ ...MONO_STYLE, fontSize: sf(28) }}
            >
              {addonDeadlineText ?? '—'}
            </p>
          </div>
          <div className="rounded-xl border border-[#1a3a2a] bg-[var(--brand-night)] p-4 text-center">
            <p
              className="font-bold text-slate-500 uppercase tracking-widest mb-1"
              style={{ fontSize: sf(10) }}
            >
              Next Break
            </p>
            <p
              className="font-black text-white leading-tight"
              style={{ ...MONO_STYLE, fontSize: sf(28) }}
            >
              {nextBreakSecs !== null ? formatTime(nextBreakSecs) : '—'}
            </p>
          </div>
        </aside>
      </div>

      {/* ── Bottom Bar (2 rows) ── */}
      <div className="flex-none flex flex-col" style={{ height: sf(100) }}>
        {/* Row 1: Players | Blinds | Ante */}
        <div
          className="flex items-center justify-between px-8"
          style={{
            height: sf(60),
            background: 'linear-gradient(180deg, #15803d 0%, #166534 100%)',
          }}
        >
          <div className="flex items-center gap-2 text-white">
            <span className="material-symbols-outlined" style={{ fontSize: sf(20) }}>
              groups
            </span>
            <span className="font-black" style={{ ...MONO_STYLE, fontSize: sf(24) }}>
              {activePlayers} / {totalEntries}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <span
              className="font-bold text-emerald-200 uppercase tracking-widest"
              style={{ fontSize: sf(10) }}
            >
              {blinds.label}
            </span>
            <span
              className="font-black text-white leading-none tracking-tight"
              style={{ ...MONO_STYLE, fontSize: sf(40) }}
            >
              {blinds.value}
            </span>
          </div>
          <div />
        </div>
        {/* Row 2: Next level | Elapsed */}
        <div
          className="flex items-center justify-between px-8 bg-[#14532d]"
          style={{ height: sf(40) }}
        >
          <div className="text-white font-bold" style={{ fontSize: sf(16) }}>
            {nextLevelNo == null ? (
              <span>
                Next Level: <span className="font-black">—</span>
              </span>
            ) : nextIsBreak ? (
              <span>
                Next: <span className="text-emerald-200">BREAK</span>
                {postBreak && (
                  <span>
                    {' '}
                    — After break: Lv {postBreak.displayLevel}:{' '}
                    {postBreak.smallBlind?.toLocaleString()} /{' '}
                    {postBreak.bigBlind?.toLocaleString()}
                    {formatForcedBets(postBreak.ante, postBreak.bba)}
                  </span>
                )}
              </span>
            ) : nextBb ? (
              <span>
                Next Level {nextDisplayLevel}:{' '}
                <span className="font-black">
                  {nextSb?.toLocaleString()} / {nextBb.toLocaleString()}
                </span>
                <span className="text-emerald-200 ml-1">{formatForcedBets(nextAnte, nextBba)}</span>
              </span>
            ) : null}
          </div>
          <div className="text-emerald-100" style={{ ...MONO_STYLE, fontSize: sf(14) }}>
            {clock}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Blind Structure Display ───────────────────────────────────────────────

function BlindStructureDisplay({
  timerPayload,
  tournament,
}: {
  timerPayload: DisplayTimerPayload | null;
  tournament: TournamentInfo | null;
}) {
  const currentLevel = timerPayload?.level ?? 1;
  const countdown = useCountdown(
    timerPayload?.remainingSeconds ?? 0,
    timerPayload?.isRunning ?? false,
  );
  const levels = tournament?.blindStructure?.levels ?? [];
  const { scale: tvScale } = useTvScale();
  const sf = (px: number) => Math.round(px * tvScale);

  return (
    <div className="bg-black text-white h-screen w-screen flex flex-col overflow-hidden">
      {/* Header 15vh */}
      <header className="h-[15vh] shrink-0 min-h-0 flex items-center gap-6 px-8 border-b-4 border-[var(--white)]/20 bg-[#0a0a05]">
        <div className="flex-1 min-w-0 h-full py-3">
          <FittedText
            text={`${tournament?.name ?? ''} Blind Structure`}
            size={sf(60)}
            color="#ffffff"
          />
        </div>
        <div className="w-[32%] h-full min-w-0 py-3">
          <TimerCountdown
            seconds={countdown}
            isBreak={false}
            theme={THEMES.custom}
            fontSize={sf(96)}
          />
        </div>
      </header>

      {/* Table */}
      <main className="flex-1 min-h-0 overflow-hidden px-8 py-6">
        <div className="w-full h-full flex flex-col">
          <div className="grid grid-cols-[repeat(14,minmax(0,1fr))] gap-4 px-8 py-6 bg-[var(--white)]/5 rounded-t-2xl border-b-2 border-[var(--white)]/30">
            {['Level', 'SB', 'BB', 'Ante', 'BBA', 'Time', 'Status'].map((h, i) => (
              <div
                key={h}
                className={`font-bold text-[var(--white)]/60 uppercase ${
                  i === 0
                    ? 'col-span-2'
                    : i < 5
                      ? 'col-span-2 text-center'
                      : i === 5
                        ? 'col-span-2 text-center'
                        : 'col-span-2 text-right'
                }`}
                style={{ fontSize: sf(36) }}
              >
                <div className="min-w-0 w-full" style={{ height: sf(36) * 1.25 }}>
                  <FittedText text={String(h)} size={sf(36)} color="inherit" />
                </div>
              </div>
            ))}
          </div>
          <div className="flex-1 min-h-0 space-y-4 pt-4 overflow-y-auto">
            {levels.map((lvl) => {
              const isActive = lvl.levelNo === currentLevel;
              const isPast = lvl.levelNo < currentLevel;
              const dlvl = getDisplayLevel(levels, lvl.levelNo);

              if (lvl.isBreak) {
                return (
                  <div
                    key={`break-${lvl.levelNo}`}
                    className="grid grid-cols-[repeat(14,minmax(0,1fr))] gap-4 px-8 bg-blue-900/40 border-2 border-blue-500/50 rounded-xl"
                    style={{ paddingTop: sf(32), paddingBottom: sf(32) }}
                  >
                    <div
                      className="col-span-2 font-bold text-blue-200"
                      style={{ fontSize: sf(48) }}
                    >
                      <div className="min-w-0 w-full" style={{ height: sf(48) * 1.25 }}>
                        <FittedText text={String('-')} size={sf(48)} color="inherit" />
                      </div>
                    </div>
                    <div
                      className="col-span-8 font-black text-blue-100 text-center tracking-[1.5em] uppercase"
                      style={{ fontSize: sf(60) }}
                    >
                      <div className="min-w-0 w-full" style={{ height: sf(60) * 1.25 }}>
                        <FittedText text={String('BREAK')} size={sf(60)} color="inherit" />
                      </div>
                    </div>
                    <div
                      className="col-span-2 font-bold text-blue-200 text-center"
                      style={{ fontSize: sf(48) }}
                    >
                      <div className="min-w-0 w-full" style={{ height: sf(48) * 1.25 }}>
                        <FittedText
                          text={String(
                            (() => {
                              const s = lvl.durationSecs ?? (lvl.durationMinutes ?? 0) * 60;
                              return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
                            })(),
                          )}
                          size={sf(48)}
                          color="inherit"
                        />
                      </div>
                    </div>
                    <div
                      className="col-span-2 font-bold text-blue-200 text-right uppercase italic"
                      style={{ fontSize: sf(36) }}
                    >
                      <div className="min-w-0 w-full" style={{ height: sf(36) * 1.25 }}>
                        <FittedText text={String(lvl.note ?? '')} size={sf(36)} color="inherit" />
                      </div>
                    </div>
                  </div>
                );
              }

              if (isActive) {
                return (
                  <div
                    key={lvl.levelNo}
                    className="grid grid-cols-[repeat(14,minmax(0,1fr))] gap-4 px-8 bg-[var(--white)] rounded-2xl transform scale-[1.02] relative z-10 border-4 border-white/20"
                    style={{
                      paddingTop: sf(40),
                      paddingBottom: sf(40),
                      boxShadow: '0 0 30px rgba(244,192,37,0.4)',
                    }}
                  >
                    <div
                      className="col-span-2 font-black text-[#0a0a05] flex items-center"
                      style={{ fontSize: sf(72) }}
                    >
                      <div className="min-w-0 w-full" style={{ height: sf(72) * 1.25 }}>
                        <FittedText text={`► ${dlvl}`} size={sf(72)} color="inherit" />
                      </div>
                    </div>
                    <div
                      className="col-span-2 font-black text-[#0a0a05] text-center"
                      style={{ fontSize: sf(72) }}
                    >
                      <div className="min-w-0 w-full" style={{ height: sf(72) * 1.25 }}>
                        <FittedText
                          text={String((lvl.smallBlind ?? 0).toLocaleString())}
                          size={sf(72)}
                          color="inherit"
                        />
                      </div>
                    </div>
                    <div
                      className="col-span-2 font-black text-[#0a0a05] text-center"
                      style={{ fontSize: sf(72) }}
                    >
                      <div className="min-w-0 w-full" style={{ height: sf(72) * 1.25 }}>
                        <FittedText
                          text={String((lvl.bigBlind ?? 0).toLocaleString())}
                          size={sf(72)}
                          color="inherit"
                        />
                      </div>
                    </div>
                    <div
                      className="col-span-2 font-black text-[#0a0a05] text-center"
                      style={{ fontSize: sf(72) }}
                    >
                      <div className="min-w-0 w-full" style={{ height: sf(72) * 1.25 }}>
                        <FittedText
                          text={String(lvl.ante && lvl.ante > 0 ? lvl.ante.toLocaleString() : '-')}
                          size={sf(72)}
                          color="inherit"
                        />
                      </div>
                    </div>
                    <div
                      className="col-span-2 font-black text-[#0a0a05] text-center"
                      style={{ fontSize: sf(72) }}
                    >
                      <div className="min-w-0 w-full" style={{ height: sf(72) * 1.25 }}>
                        <FittedText
                          text={String(lvl.bba && lvl.bba > 0 ? lvl.bba.toLocaleString() : '-')}
                          size={sf(72)}
                          color="inherit"
                        />
                      </div>
                    </div>
                    <div
                      className="col-span-2 font-black text-[#0a0a05] text-center"
                      style={{ fontSize: sf(72) }}
                    >
                      <div className="min-w-0 w-full" style={{ height: sf(72) * 1.25 }}>
                        <FittedText
                          text={String(
                            (() => {
                              const s = lvl.durationSecs ?? (lvl.durationMinutes ?? 0) * 60;
                              return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
                            })(),
                          )}
                          size={sf(72)}
                          color="inherit"
                        />
                      </div>
                    </div>
                    <div
                      className="col-span-2 font-black text-[#0a0a05] text-right uppercase flex items-center justify-end"
                      style={{ fontSize: sf(48) }}
                    >
                      <div className="min-w-0 w-full" style={{ height: sf(48) * 1.25 }}>
                        <FittedText text={String('ACTIVE')} size={sf(48)} color="inherit" />
                      </div>
                    </div>
                  </div>
                );
              }

              return (
                <div
                  key={lvl.levelNo}
                  className={`grid grid-cols-[repeat(14,minmax(0,1fr))] gap-4 px-8 rounded-xl border ${
                    isPast ? 'bg-white/5 opacity-30 border-white/5' : 'bg-white/5 border-white/10'
                  }`}
                  style={{ paddingTop: sf(24), paddingBottom: sf(24) }}
                >
                  <div
                    className={`col-span-2 ${isPast ? 'font-medium' : 'font-bold text-white'}`}
                    style={{ fontSize: sf(48) }}
                  >
                    <div className="min-w-0 w-full" style={{ height: sf(48) * 1.25 }}>
                      <FittedText text={String(dlvl)} size={sf(48)} color="inherit" />
                    </div>
                  </div>
                  <div
                    className="col-span-2 font-bold text-white text-center"
                    style={{ fontSize: sf(48) }}
                  >
                    <div className="min-w-0 w-full" style={{ height: sf(48) * 1.25 }}>
                      <FittedText
                        text={String((lvl.smallBlind ?? 0).toLocaleString())}
                        size={sf(48)}
                        color="inherit"
                      />
                    </div>
                  </div>
                  <div
                    className="col-span-2 font-bold text-white text-center"
                    style={{ fontSize: sf(48) }}
                  >
                    <div className="min-w-0 w-full" style={{ height: sf(48) * 1.25 }}>
                      <FittedText
                        text={String((lvl.bigBlind ?? 0).toLocaleString())}
                        size={sf(48)}
                        color="inherit"
                      />
                    </div>
                  </div>
                  <div
                    className="col-span-2 font-bold text-white text-center"
                    style={{ fontSize: sf(48) }}
                  >
                    <div className="min-w-0 w-full" style={{ height: sf(48) * 1.25 }}>
                      <FittedText
                        text={String(lvl.ante && lvl.ante > 0 ? lvl.ante.toLocaleString() : '-')}
                        size={sf(48)}
                        color="inherit"
                      />
                    </div>
                  </div>
                  <div
                    className="col-span-2 font-bold text-white text-center"
                    style={{ fontSize: sf(48) }}
                  >
                    <div className="min-w-0 w-full" style={{ height: sf(48) * 1.25 }}>
                      <FittedText
                        text={String(lvl.bba && lvl.bba > 0 ? lvl.bba.toLocaleString() : '-')}
                        size={sf(48)}
                        color="inherit"
                      />
                    </div>
                  </div>
                  <div
                    className="col-span-2 font-bold text-white text-center"
                    style={{ fontSize: sf(48) }}
                  >
                    <div className="min-w-0 w-full" style={{ height: sf(48) * 1.25 }}>
                      <FittedText
                        text={String(
                          (() => {
                            const s = lvl.durationSecs ?? (lvl.durationMinutes ?? 0) * 60;
                            return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
                          })(),
                        )}
                        size={sf(48)}
                        color="inherit"
                      />
                    </div>
                  </div>
                  <div
                    className="col-span-2 font-bold text-white text-right uppercase"
                    style={{ fontSize: sf(36) }}
                  >
                    <div className="min-w-0 w-full" style={{ height: sf(36) * 1.25 }}>
                      <FittedText
                        text={String(isPast ? 'Done' : '')}
                        size={sf(36)}
                        color="inherit"
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </main>
    </div>
  );
}

// ─── Leaderboard Display ────────────────────────────────────────────────────

function LeaderboardDisplay({
  leaderboard,
  timerPayload,
  tournament,
  logo,
}: {
  leaderboard: LeaderboardEntry[];
  timerPayload: DisplayTimerPayload | null;
  tournament: TournamentInfo | null;
  logo: string | null;
}) {
  const name = tournament?.name ?? timerPayload?.tournamentName ?? '';
  const activePlayers = timerPayload?.activePlayers ?? leaderboard.length;
  const totalEntries = timerPayload?.totalEntries ?? 0;
  const prizePool = getDisplayPrizePool(timerPayload, tournament);

  return (
    <div className="w-full h-full bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex flex-col text-white font-sans">
      <header className="shrink-0 flex items-center gap-4 px-8 py-4 border-b border-slate-700/50">
        {logo && <img src={logo} alt="" className="h-10 max-w-[10%] object-contain" />}
        <div className="flex-1 min-w-0">
          <div className="h-10">
            <FittedText text={name} size={24} color="#ffffff" />
          </div>
          <div className="h-6">
            <FittedText
              text={`${activePlayers}/${totalEntries} Players · Prize Pool $${Number(prizePool).toLocaleString()}`}
              size={14}
              color="#94a3b8"
            />
          </div>
        </div>
        <div className="w-[25%] min-w-0">
          <div className="h-12">
            <FittedText
              text={timerPayload ? formatTimerClock(timerPayload.remainingSeconds) : '--:--'}
              size={30}
              color="#fbbf24"
            />
          </div>
          <div className="h-5">
            <FittedText text={`Level ${timerPayload?.level ?? '-'}`} size={12} color="#94a3b8" />
          </div>
        </div>
      </header>

      {/* Leaderboard Table */}
      <div className="flex-1 min-h-0 overflow-y-auto px-8 py-4">
        {leaderboard.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center text-slate-500">
              <span className="text-6xl block mb-4">🏆</span>
              <p className="text-xl">Waiting for players...</p>
            </div>
          </div>
        ) : (
          <div
            className="grid gap-1.5"
            style={{ gridTemplateRows: `repeat(${Math.min(leaderboard.length, 10)}, 1fr)` }}
          >
            {leaderboard.slice(0, 20).map((entry, i) => {
              const isTop3 = i < 3;
              const colors = [
                'text-amber-400 bg-amber-400/10 border-amber-400/30',
                'text-slate-300 bg-slate-300/10 border-slate-400/30',
                'text-orange-400 bg-orange-400/10 border-orange-400/30',
              ];
              const rowColor = isTop3
                ? colors[i]
                : 'text-slate-300 bg-slate-800/30 border-slate-700/30';

              return (
                <div
                  key={`${entry.rank}-${entry.playerName}`}
                  className={`flex items-center gap-4 px-5 py-2.5 rounded-xl border transition-all ${rowColor}`}
                >
                  <div
                    className={`text-2xl font-bold font-mono w-10 text-center ${isTop3 ? '' : 'text-slate-500'}`}
                  >
                    <div className="h-8">
                      <FittedText text={String(entry.rank)} size={24} color="inherit" />
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="h-8">
                      <FittedText text={entry.playerName} size={18} color="inherit" />
                    </div>
                    {entry.tableNo && (
                      <div className="h-5">
                        <FittedText
                          text={`T${entry.tableNo} S${entry.seatNo}`}
                          size={12}
                          color="#64748b"
                        />
                      </div>
                    )}
                  </div>
                  <div className="w-[35%] min-w-0 h-9">
                    <FittedText text={entry.chips.toLocaleString()} size={20} color="inherit" />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-8 py-3 border-t border-slate-700/50 flex justify-between text-xs text-slate-500">
        <span>LIVE LEADERBOARD</span>
        <span>{new Date().toLocaleTimeString('en-US', { hour12: false })}</span>
      </div>
    </div>
  );
}

// ─── Prize Table Display ───────────────────────────────────────────────────

function PrizeTableDisplay({
  tournament,
  timerPayload,
}: {
  tournament: TournamentInfo | null;
  timerPayload: DisplayTimerPayload | null;
}) {
  const slots = tournament?.payoutSlots ?? [];
  const prizePool = getDisplayPrizePool(timerPayload, tournament);
  const { scale: tvScale } = useTvScale();
  const sf = (px: number) => Math.round(px * tvScale);

  const rankLabel = (rank: number) => {
    if (rank === 1) return '1st';
    if (rank === 2) return '2nd';
    if (rank === 3) return '3rd';
    return `${rank}th`;
  };

  const rankGradient = (rank: number) => {
    if (rank === 1) return 'linear-gradient(90deg, rgba(242,185,13,0.15) 0%, transparent 100%)';
    if (rank === 2) return 'linear-gradient(90deg, rgba(224,224,224,0.1) 0%, transparent 100%)';
    if (rank === 3) return 'linear-gradient(90deg, rgba(205,127,50,0.1) 0%, transparent 100%)';
    return '';
  };

  return (
    <div
      className="text-white h-screen w-screen flex flex-col overflow-hidden relative"
      style={{ background: 'var(--brand-night)', fontFamily: 'var(--font-body)', padding: sf(48) }}
    >
      <div className="absolute inset-0 opacity-10 pointer-events-none">
        <svg width="100%" height="100%">
          <defs>
            <pattern id="grid" width="100" height="100" patternUnits="userSpaceOnUse">
              <path d="M 100 0 L 0 0 0 100" fill="none" stroke="#f2b90d" strokeWidth="1" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid)" />
        </svg>
      </div>

      <header
        className="relative z-10 shrink-0 flex items-center gap-6 border-b-2 border-[var(--white)]/30"
        style={{ paddingBottom: sf(40), marginBottom: sf(48) }}
      >
        <div className="flex-1 min-w-0">
          <div style={{ height: sf(36) }}>
            <FittedText text={tournament?.name ?? ''} size={sf(24)} color="#ffffff" />
          </div>
          <div style={{ height: sf(80) }}>
            <FittedText text="Prize Structure" size={sf(60)} color="#ffffff" />
          </div>
        </div>
        <div className="w-[40%] min-w-0">
          <div style={{ height: sf(32) }}>
            <FittedText text="Total Prize Pool" size={sf(20)} color="#94a3b8" />
          </div>
          <div style={{ height: sf(72) }}>
            <FittedText text={`NT$ ${prizePool.toLocaleString()}`} size={sf(48)} color="#ffffff" />
          </div>
        </div>
      </header>

      <main className="relative z-10 flex-1 min-h-0 overflow-hidden flex flex-col">
        <div className="w-full rounded-2xl border border-[#3d3516] bg-[#1a170a]/50 flex-1 min-h-0 overflow-y-auto">
          <table className="w-full table-fixed border-collapse">
            <colgroup>
              <col style={{ width: '30%' }} />
              <col style={{ width: '50%' }} />
              <col style={{ width: '20%' }} />
            </colgroup>
            <thead>
              <tr className="bg-[#1a170a] text-[var(--white)] border-b border-[#3d3516] sticky top-0 z-10">
                <th
                  className="px-4 text-left font-bold uppercase tracking-wider"
                  style={{ fontSize: sf(24), paddingTop: sf(32), paddingBottom: sf(32) }}
                >
                  Rank
                </th>
                <th
                  className="px-4 text-left font-bold uppercase tracking-wider"
                  style={{ fontSize: sf(24), paddingTop: sf(32), paddingBottom: sf(32) }}
                >
                  Prize
                </th>
                <th
                  className="px-4 text-right font-bold uppercase tracking-wider"
                  style={{ fontSize: sf(24), paddingTop: sf(32), paddingBottom: sf(32) }}
                >
                  %
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#3d3516]/50">
              {slots.map((slot) => (
                <tr key={slot.rank} style={{ background: rankGradient(slot.rank) || undefined }}>
                  <td className="px-4" style={{ paddingTop: sf(32), paddingBottom: sf(32) }}>
                    <div className="flex items-center gap-4 min-w-0">
                      <span
                        className="material-symbols-outlined shrink-0"
                        style={{
                          fontSize: sf(48),
                          color: slot.rank <= 3 ? 'var(--white)' : '#94a3b8',
                        }}
                      >
                        workspace_premium
                      </span>
                      <div className="flex-1 min-w-0" style={{ height: sf(40) }}>
                        <FittedText text={rankLabel(slot.rank)} size={sf(30)} color="#ffffff" />
                      </div>
                    </div>
                  </td>
                  <td className="px-4" style={{ paddingTop: sf(32), paddingBottom: sf(32) }}>
                    <div
                      style={{
                        height: sf(48),
                        textShadow: slot.rank === 1 ? '0 0 20px rgba(242,185,13,0.4)' : 'none',
                      }}
                    >
                      <FittedText
                        text={`NT$ ${Number(slot.amount).toLocaleString()}`}
                        size={sf(36)}
                        color={slot.rank <= 3 ? 'var(--white)' : '#f1f5f9'}
                      />
                    </div>
                  </td>
                  <td className="px-4" style={{ paddingTop: sf(32), paddingBottom: sf(32) }}>
                    <div style={{ height: sf(40) }}>
                      <FittedText
                        text={`${slot.percentage}%`}
                        size={sf(30)}
                        color={slot.rank <= 3 ? 'var(--white)' : '#94a3b8'}
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
}

// ─── Fullscreen Overlay ─────────────────────────────────────────────────────

function FullscreenOverlay({ onEnter, onDismiss }: { onEnter: () => void; onDismiss: () => void }) {
  return (
    <div
      className="fixed inset-0 z-[9999] bg-black/90 flex flex-col items-center justify-center cursor-pointer"
      onClick={onEnter}
    >
      <span
        className="material-symbols-outlined text-[var(--white)] mb-6"
        style={{ fontSize: '80px' }}
      >
        fullscreen
      </span>
      <p className="text-white text-4xl font-bold mb-4">Tap to enter fullscreen</p>
      <p className="text-slate-400 text-lg mb-8">For the best viewing experience</p>
      <button
        onClick={(e) => {
          e.stopPropagation();
          onDismiss();
        }}
        className="text-slate-600 text-sm underline hover:text-slate-400 transition-colors"
      >
        Skip
      </button>
    </div>
  );
}

// ─── Floating Settings Menu ─────────────────────────────────────────────────

function FloatingMenu({
  screenName,
  connected,
  isFullscreen,
  isFullscreenSupported,
  onToggleFullscreen,
  onSwitchScreen,
}: {
  screenName: string;
  connected: boolean;
  isFullscreen: boolean;
  isFullscreenSupported: boolean;
  onToggleFullscreen: () => void;
  onSwitchScreen: () => void;
}) {
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const resetHideTimer = useCallback(() => {
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    setVisible(true);
    hideTimerRef.current = setTimeout(() => {
      setVisible(false);
      setMenuOpen(false);
    }, 5000);
  }, []);

  useEffect(() => {
    const onActivity = () => resetHideTimer();
    window.addEventListener('mousemove', onActivity);
    window.addEventListener('keydown', onActivity);
    window.addEventListener('touchstart', onActivity);
    return () => {
      window.removeEventListener('mousemove', onActivity);
      window.removeEventListener('keydown', onActivity);
      window.removeEventListener('touchstart', onActivity);
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    };
  }, [resetHideTimer]);

  if (!visible) return null;

  return (
    <div className="fixed bottom-4 right-4 z-[9998]">
      {menuOpen && (
        <div
          className="backdrop-blur-xs border border-[#333] rounded-xl p-4 mb-2 min-w-[200px] shadow-2xl"
          style={{ backgroundColor: 'rgba(26, 26, 26, 0.95)' }}
        >
          <p className="text-white font-bold text-sm mb-3 truncate">{screenName}</p>
          <div className="flex items-center gap-2 mb-1 text-sm">
            <span
              className="inline-block w-2 h-2 rounded-full"
              style={{ backgroundColor: connected ? '#22c55e' : '#ef4444' }}
            />
            <span className="text-[#94a3b8]">{connected ? 'Connected' : 'Disconnected'}</span>
          </div>
          <p className="text-[#64748b] text-xs mb-3 pl-4">
            {window.innerWidth}x{window.innerHeight} · DPR {window.devicePixelRatio}
          </p>
          <div className="space-y-1">
            {isTvAcceptanceEnabled && (
              <button
                onClick={() => {
                  saveDisplaySample();
                  navigate('/tv/acceptance');
                }}
                className="w-full text-left px-3 py-2 rounded-lg text-amber-300 focus:outline focus:outline-2 focus:outline-amber-300"
              >
                TV 實機驗收與畫面校正
              </button>
            )}
            <button
              onClick={onSwitchScreen}
              className="w-full text-left px-3 py-2 rounded-lg text-[#cbd5e1] hover:bg-[#333] hover:text-white transition-colors text-sm flex items-center gap-2"
            >
              <span className="material-symbols-outlined text-base">swap_horiz</span>
              Switch Screen
            </button>
            {isFullscreenSupported && (
              <button
                onClick={onToggleFullscreen}
                className="w-full text-left px-3 py-2 rounded-lg text-[#cbd5e1] hover:bg-[#333] hover:text-white transition-colors text-sm flex items-center gap-2"
              >
                <span className="material-symbols-outlined text-base">
                  {isFullscreen ? 'fullscreen_exit' : 'fullscreen'}
                </span>
                {isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
              </button>
            )}
          </div>
        </div>
      )}
      <button
        onClick={() => setMenuOpen((v) => !v)}
        className="w-12 h-12 rounded-full bg-[#1a1a1ab3] backdrop-blur-xs border border-[#33333380] flex items-center justify-center hover:bg-[#333333cc] transition-colors ml-auto"
      >
        <span className="material-symbols-outlined text-[#94a3b8] text-xl">settings</span>
      </button>
    </div>
  );
}

// ─── Main TVScreen Router ──────────────────────────────────────────────────

export default function TVScreen() {
  const [screenInset, setScreenInset] = useState(0);
  return (
    <TvSafeArea screenInset={screenInset}>
      <TVScreenContent onInsetChange={setScreenInset} />
    </TvSafeArea>
  );
}

function TVScreenContent({ onInsetChange }: { onInsetChange: (value: number) => void }) {
  const navigate = useNavigate();
  const [credential] = useState(readDisplaySessionCredential);
  const [screen, setScreen] = useState<DisplayScreen | null>(null);
  const [tournament, setTournament] = useState<TournamentInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const logo = useVenueLogo(screen?.venue?.code);
  const customConfig = useTimerLayoutConfig();
  const screenLoadedRef = useRef(false);
  const prevTournamentIdRef = useRef<string | null>(null);
  const fetchCountRef = useRef(0);
  const tournamentFetchRef = useRef(0);

  const { isFullscreen, isSupported: isFullscreenSupported, toggleFullscreen } = useFullscreen();
  const siteName = useSiteName();
  useEffect(() => {
    document.title = siteName;
  }, [siteName]);

  // Fullscreen overlay state
  const [showFsOverlay, setShowFsOverlay] = useState(() => {
    try {
      return !localStorage.getItem('tv-fullscreen-dismissed');
    } catch {
      return false;
    }
  });

  const apiBase =
    (import.meta as unknown as { env: { VITE_API_URL?: string } }).env.VITE_API_URL ?? '';

  const { timerPayload, connected, configUpdate, leaderboard, reconnectCount } = useDisplaySocket({
    credential,
    enabled: !!credential && !!screen,
    audioConfig: timerAudioFromOptions(customConfig.options),
    audioReady: customConfig.audioReady,
  });

  // ─── REST fetch helper (reused by polling, reconnect, and recovery) ───
  const fetchScreenConfig = (_reason: string) => {
    if (!credential) return;
    fetchCountRef.current++;
    const fetchId = fetchCountRef.current;
    fetch(`${apiBase}/api/display/session`, {
      headers: { Authorization: `Bearer ${credential}` },
      signal: timeoutSignal(15000),
    })
      .then((res) => {
        if (!res.ok) {
          if (res.status === 401 || res.status === 403 || res.status === 404) {
            if (fetchCountRef.current === fetchId) {
              if (res.status === 401 || res.status === 403) clearDisplaySessionCredential();
              setScreen(null);
              onInsetChange(0);
              setTournament(null);
              setError('Screen unavailable. Pair this screen again.');
            }
          }
          throw new Error('Screen not found');
        }
        return res.json() as Promise<{ success?: boolean; data?: DisplayScreen } | DisplayScreen>;
      })
      .then((json) => {
        if (fetchCountRef.current !== fetchId) return; // stale
        // API wraps response in { success, data } envelope — unwrap it
        const data = ('success' in json && json.data ? json.data : json) as DisplayScreen;
        screenLoadedRef.current = true;
        setError(null);
        onInsetChange(data.insetPercent ?? 0);
        setScreen((prev) => {
          if (
            !prev ||
            prev.tournamentId !== data.tournamentId ||
            prev.layout !== data.layout ||
            prev.insetPercent !== data.insetPercent ||
            prev.venue?.code !== data.venue?.code
          ) {
            return data;
          }
          return prev;
        });
      })
      .catch(() => {
        if (fetchCountRef.current !== fetchId) return;
        // Only set error if screen was NEVER loaded (initial load failure)
        if (!screenLoadedRef.current) {
          setError('Screen not found');
        }
      });
  };

  // Handle WS config updates (admin assigns/changes tournament)
  useEffect(() => {
    if (!configUpdate) return;
    if (configUpdate.insetPercent !== undefined) onInsetChange(configUpdate.insetPercent);
    // If WS says tournamentId is null but we previously had one, verify via REST first
    if (!configUpdate.tournamentId && prevTournamentIdRef.current) {
      fetchScreenConfig('ws-null-verify');
      return;
    }
    setScreen((prev) => {
      if (!prev) return prev;
      if (
        prev.tournamentId === configUpdate.tournamentId &&
        prev.layout === configUpdate.layout &&
        (configUpdate.insetPercent === undefined || prev.insetPercent === configUpdate.insetPercent)
      )
        return prev;
      return {
        ...prev,
        tournamentId: configUpdate.tournamentId,
        layout: configUpdate.layout,
        insetPercent: configUpdate.insetPercent ?? prev.insetPercent,
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configUpdate]);

  // On WS reconnect, immediately re-fetch screen config from REST
  useEffect(() => {
    if (reconnectCount > 0) {
      fetchScreenConfig('ws-reconnect');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reconnectCount]);

  // Periodic polling (every 15 seconds) — initial load + fallback
  useEffect(() => {
    fetchCountRef.current++;
    tournamentFetchRef.current++;
    screenLoadedRef.current = false;
    prevTournamentIdRef.current = null;
    setScreen(null);
    onInsetChange(0);
    setTournament(null);
    setError(null);
    if (!credential) {
      setError('Pairing required. Enter a pairing code to use this screen.');
      return;
    }
    fetchScreenConfig('initial');
    const interval = setInterval(() => fetchScreenConfig('poll'), 15000);
    const invalidateRequests = () => {
      fetchCountRef.current++;
      tournamentFetchRef.current++;
    };
    return () => {
      clearInterval(interval);
      invalidateRequests();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [credential, apiBase]);

  // Auto-recovery: if tournamentId was set and becomes null, re-fetch immediately
  useEffect(() => {
    const currentTid = screen?.tournamentId ?? null;
    const prevTid = prevTournamentIdRef.current;
    prevTournamentIdRef.current = currentTid;
    if (prevTid && !currentTid) {
      console.warn(`[TVScreen] Tournament lost (${prevTid} → null), recovering...`);
      fetchScreenConfig('recovery');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screen?.tournamentId]);

  // Refresh duration metadata after timer changes and when completion occurs while paused.
  useEffect(() => {
    const tid = screen?.tournamentId;
    setTournament((current) => (current?.id === tid ? current : null));
    tournamentFetchRef.current++;
    if (!tid) return;
    const fetchTournament = () => {
      const fetchId = ++tournamentFetchRef.current;
      fetch(`${apiBase}/api/display/session/tournament`, {
        headers: { Authorization: `Bearer ${credential}` },
        signal: timeoutSignal(10000),
      })
        .then((res) =>
          res.ok
            ? (res.json() as Promise<{ success?: boolean; data?: TournamentInfo } | TournamentInfo>)
            : null,
        )
        .then((json) => {
          if (tournamentFetchRef.current !== fetchId || !json) return;
          const data = ('success' in json && json.data ? json.data : json) as TournamentInfo;
          setTournament(data);
        })
        .catch((err) => console.warn('[TVScreen] Failed to fetch tournament:', err));
    };
    fetchTournament();
    const interval = setInterval(fetchTournament, 15000);
    return () => {
      tournamentFetchRef.current++;
      clearInterval(interval);
    };
  }, [screen?.tournamentId, apiBase, credential, timerPayload?.isRunning]);

  // Double-click to toggle fullscreen
  useEffect(() => {
    if (!isFullscreenSupported) return;
    const handler = () => {
      toggleFullscreen().catch(() => {});
    };
    document.addEventListener('dblclick', handler);
    return () => document.removeEventListener('dblclick', handler);
  }, [isFullscreenSupported, toggleFullscreen]);

  const handleFsOverlayEnter = async () => {
    try {
      await toggleFullscreen();
    } catch {
      /* ignored */
    }
    setShowFsOverlay(false);
  };

  const handleFsOverlayDismiss = () => {
    setShowFsOverlay(false);
    try {
      localStorage.setItem('tv-fullscreen-dismissed', '1');
    } catch {
      /* Safari private mode */
    }
  };

  const handleSwitchScreen = () =>
    navigate(screen?.venue?.code ? `/tv?venue=${encodeURIComponent(screen.venue.code)}` : '/tv');

  // ─── Overlay layer (renders on top of any display mode) ───
  const overlayLayer = (
    <>
      {showFsOverlay && !isFullscreen && isFullscreenSupported && (
        <FullscreenOverlay onEnter={handleFsOverlayEnter} onDismiss={handleFsOverlayDismiss} />
      )}
      {screen && (
        <FloatingMenu
          screenName={screen.name}
          connected={connected}
          isFullscreen={isFullscreen}
          isFullscreenSupported={isFullscreenSupported}
          onToggleFullscreen={() => toggleFullscreen().catch(() => {})}
          onSwitchScreen={handleSwitchScreen}
        />
      )}
    </>
  );

  if (error) {
    return (
      <div className="bg-black h-screen w-screen flex flex-col items-center justify-center text-white">
        <span className="material-symbols-outlined text-8xl text-red-500 mb-4">tv_off</span>
        <p className="text-4xl font-bold">{error}</p>
        <p className="text-slate-400 mt-2 text-xl">請重新選擇場館與電視</p>
        <button
          onClick={() => navigate('/tv')}
          className="mt-8 rounded-xl bg-amber-400 px-8 py-4 text-xl font-bold text-slate-950 focus:outline-4 focus:outline-white"
        >
          選擇場館與電視
        </button>
      </div>
    );
  }

  if (!screen) {
    return (
      <div className="bg-black h-screen w-screen flex items-center justify-center">
        <div className="text-[var(--white)] text-4xl animate-pulse">Loading...</div>
      </div>
    );
  }

  if (!screen.tournamentId) {
    return (
      <>
        {overlayLayer}
        <div className="bg-[var(--brand-night)] h-screen w-screen flex flex-col items-center justify-center text-white">
          <span className="material-symbols-outlined text-8xl text-[var(--white)] mb-6">tv</span>
          <p className="text-xl mb-2">{screen.venue?.name}</p>
          {logo && <img src={logo} alt="Venue logo" className="max-h-24 mb-6" />}
          <p className="text-3xl font-bold">{screen.name}</p>
          <p className="text-slate-400 mt-2 text-xl">No tournament assigned</p>
          {!connected && <p className="text-slate-600 mt-4 text-sm">Connecting...</p>}
        </div>
      </>
    );
  }

  const layout = screen.layout;

  if (layout === 'LEADERBOARD') {
    return (
      <>
        {overlayLayer}
        <LeaderboardDisplay
          leaderboard={leaderboard}
          timerPayload={timerPayload}
          tournament={tournament}
          logo={logo}
        />
      </>
    );
  }

  if (layout === 'BLIND_STRUCTURE') {
    return (
      <>
        {overlayLayer}
        <BlindStructureDisplay timerPayload={timerPayload} tournament={tournament} />
      </>
    );
  }

  if (layout === 'PRIZE_TABLE') {
    return (
      <>
        {overlayLayer}
        <PrizeTableDisplay tournament={tournament} timerPayload={timerPayload} />
      </>
    );
  }

  return (
    <>
      {overlayLayer}
      <GridTimerDisplay
        timerPayload={timerPayload}
        tournament={tournament}
        logo={logo}
        config={customConfig}
      />
    </>
  );
}
